import {
    useState, useEffect, useRef, useCallback,
} from 'react';
import useAuthContext from 'contexts/AuthContext';
import { SensitivityAnalysisParams, SensitivityAnalysisResponse } from '../types';
import useDispatchSensitivityAnalysis from './useDispatchSensitivityAnalysis';
import useSensitivityTaskStatus from './useSensitivityTaskStatus';

/**
 * Dispatches a sensitivity analysis Celery task, then polls for completion.
 */
export const useGetProFormaSensitivityAnalysis = (
    dealId: number,
    proFormaId: number,
    params: SensitivityAnalysisParams,
    sourceId: number | null,
    options: { enabled: boolean } = { enabled: true },
) => {
    const { selectedOrganizationId } = useAuthContext();
    const [taskId, setTaskId] = useState<string | null>(null);
    const [data, setData] = useState<SensitivityAnalysisResponse | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const dispatchIdRef = useRef(0);

    const dispatchMutation = useDispatchSensitivityAnalysis();

    const dispatch = useCallback((): Promise<{ taskId: string } | void> => {
        if (!options.enabled) return Promise.resolve();

        setIsLoading(true);
        setTaskId(null);
        setError(null);
        setData(undefined);

        const thisId = ++dispatchIdRef.current;
        const variables = { dealId, proFormaId, params, sourceId };
        return dispatchMutation
            .mutateAsync(variables)
            .then((result) => {
                if (thisId === dispatchIdRef.current) {
                    setTaskId(result.taskId);
                }
                return result;
            })
            .catch((err) => {
                if (thisId === dispatchIdRef.current) {
                    setIsLoading(false);
                    setError(err);
                }
                throw err;
            });
    }, [options.enabled, dispatchMutation, dealId, proFormaId, params, sourceId]);    

    const paramsKey = JSON.stringify({
        dealId, proFormaId, params, sourceId,
    });

    const prevParamsKeyRef = useRef<string>('');
    useEffect(() => {
        if (paramsKey !== prevParamsKeyRef.current && options.enabled) {
            prevParamsKeyRef.current = paramsKey;
            dispatch().catch((reason) => { setError(new Error(`Failed to dispatch sensitivity analysis`, { cause: reason })); });  
        }
    }, [paramsKey, options.enabled, dispatch]);

    const taskStatusQuery = useSensitivityTaskStatus({
        taskId,
        organizationId: selectedOrganizationId!,
        enabled: !!taskId && !!selectedOrganizationId && options.enabled,
    });

    useEffect(() => {
        if (taskStatusQuery.data?.status === 'SUCCESS' && taskStatusQuery.data.result) {
            setData(taskStatusQuery.data.result);
            setIsLoading(false);
            setTaskId(null);
        } else if (taskStatusQuery.data?.status === 'FAILURE') {
            setIsLoading(false);
            setTaskId(null);
            const message = taskStatusQuery.data.timedOut
                ? 'Sensitivity analysis timed out'
                : taskStatusQuery.data.message;
            setError(new Error(message));
        } else if (taskStatusQuery.isError) {  
            setIsLoading(false);  
            setTaskId(null);  
            const err = taskStatusQuery.error as unknown;  
            if (err instanceof Error) {  
                setError(err);  
            } else {  
                setError(new Error('Failed to fetch sensitivity analysis status', { cause: err }));
            }
        }
    }, [taskStatusQuery.data?.status, 
        taskStatusQuery.data?.result, 
        taskStatusQuery.data?.message, 
        taskStatusQuery.data?.timedOut, 
        taskStatusQuery.isError, 
        taskStatusQuery.error]);

    const isFetching = isLoading || (!!taskId && taskStatusQuery.isFetching);

    return {
        data,
        isLoading,
        isFetching,
        refetch: dispatch,
        error,
    };
};

export default useGetProFormaSensitivityAnalysis;
